import React, { useEffect, useState, useRef, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import Modal from "react-modal";
import StartChatModal from "./StartChatModal";
import ChatModal from "./ChatModal";
import ChatList from "./ChatList";
import ChatView from "./ChatView";
import {
  createIndividualChat,
  setChatAsAdminSupport,
  subscribeToUserChats,
} from "../../../Services/messagingService";
import {
  getFamilyTreeIdFromStorage,
  resolveAdminDetailsForSupport,
} from "../../../Utils/headOfLineRole";

function Message() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [showPopup, setShowPopup] = useState(false);
  const [chatMode, setChatMode] = useState("");
  const [editGroupChat, setEditGroupChat] = useState(null);
  const [familyTreeId, setFamilyTreeId] = useState("");
  const [currentUserId, setCurrentUserId] = useState(null);
  const [chats, setChats] = useState([]);
  const [chatsLoading, setChatsLoading] = useState(true);
  const [selectedChat, setSelectedChat] = useState(null);
  const [hasChats, setHasChats] = useState(false);
  const [isOpeningAdminSupport, setIsOpeningAdminSupport] = useState(false);
  const [isChatListCollapsed, setIsChatListCollapsed] = useState(false);
  const [chatColumnWidthPx, setChatColumnWidthPx] = useState(null);
  const [isMobileMessages, setIsMobileMessages] = useState(() =>
    typeof window !== "undefined" && window.matchMedia("(max-width: 768px)").matches
  );
  const [mobileShowChat, setMobileShowChat] = useState(false);
  const layoutShellRef = useRef(null);
  const chatColumnRef = useRef(null);
  const resizeDragRef = useRef(null);
  const pendingAdminSupportChatIdRef = useRef(null);

  const getChatColumnBounds = useCallback(() => {
    const shell = layoutShellRef.current;
    const aside = document.getElementById("messages-chat-list-panel");
    if (!shell) return { min: 0, max: 0 };
    const shellW = shell.getBoundingClientRect().width;
    const asideW = aside ? aside.getBoundingClientRect().width : 0;
    const max = Math.max(0, shellW - asideW);
    const halfScreen = typeof window !== "undefined" ? window.innerWidth * 0.5 : max;
    const halfOfStrip = max * 0.5;
    const min = Math.min(halfScreen, halfOfStrip);
    return { min, max };
  }, []);

  const handleResizePointerDown = useCallback((e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    const col = chatColumnRef.current;
    const shell = layoutShellRef.current;
    if (!col || !shell) return;

    const startX = e.clientX;
    const startWidth = col.getBoundingClientRect().width;
    const { min, max } = getChatColumnBounds();

    resizeDragRef.current = {
      startX,
      startWidth,
      min,
      max,
    };

    const onMove = (ev) => {
      const drag = resizeDragRef.current;
      if (!drag) return;
      const dx = drag.startX - ev.clientX;
      const next = Math.min(drag.max, Math.max(drag.min, drag.startWidth + dx));
      setChatColumnWidthPx(next);
    };

    const onUp = () => {
      resizeDragRef.current = null;
      document.body.style.removeProperty("user-select");
      document.removeEventListener("pointermove", onMove);
      document.removeEventListener("pointerup", onUp);
      document.removeEventListener("pointercancel", onUp);
    };

    document.body.style.userSelect = "none";
    document.addEventListener("pointermove", onMove);
    document.addEventListener("pointerup", onUp);
    document.addEventListener("pointercancel", onUp);
  }, [getChatColumnBounds]);

  const handleResizeDoubleClick = useCallback(() => {
    setChatColumnWidthPx(null);
  }, []);

  useEffect(() => {
    if (chatColumnWidthPx == null) return;
    const clamp = () => {
      const { min, max } = getChatColumnBounds();
      setChatColumnWidthPx((w) => {
        if (w == null) return w;
        return Math.min(max, Math.max(min, w));
      });
    };
    clamp();
    window.addEventListener("resize", clamp);
    return () => window.removeEventListener("resize", clamp);
  }, [chatColumnWidthPx, getChatColumnBounds, isChatListCollapsed]);

  useEffect(() => {
    if (!isChatListCollapsed) {
      setChatColumnWidthPx(null);
    }
  }, [isChatListCollapsed]);

  // Mobile: list-first; desktop keeps side-by-side layout
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    const apply = () => {
      const mobile = mq.matches;
      setIsMobileMessages(mobile);
      if (!mobile) {
        setMobileShowChat(false);
      }
    };
    apply();
    mq.addEventListener("change", apply);
    return () => mq.removeEventListener("change", apply);
  }, []);

  useEffect(() => {
    const localUser = JSON.parse(localStorage.getItem("userDetail") || "{}");
    setFamilyTreeId(getFamilyTreeIdFromStorage() || "");
    const userId = localUser?.user_id || localUser?.id || null;
    setCurrentUserId(userId);
  }, []);

  // When opening messages with ?open=admin-support: ensure Admin Support chat exists and select it
  useEffect(() => {
    const openAdminSupport = searchParams.get("open") === "admin-support";
    if (!openAdminSupport || !currentUserId || !familyTreeId) return;

    const ensureAdminSupportChat = async () => {
      setIsOpeningAdminSupport(true);
      try {
        const adminDetails = await resolveAdminDetailsForSupport(familyTreeId);
        const adminId = adminDetails?.id;

        if (!adminId) {
          setIsOpeningAdminSupport(false);
          return;
        }
        const currentUserInfo = null;
        const adminInfo = {
          user_id: adminId,
          name: adminDetails.name || "Admin",
          first_name: (adminDetails.name || "Admin").split(" ")[0] || "Admin",
        };
        const chatId = await createIndividualChat(
          currentUserId,
          adminId,
          familyTreeId,
          currentUserInfo,
          adminInfo
        );
        await setChatAsAdminSupport(chatId);
        pendingAdminSupportChatIdRef.current = chatId;
      } catch (err) {
        console.error("Error creating Admin Support chat:", err);
        setIsOpeningAdminSupport(false);
      }
    };

    ensureAdminSupportChat();
  }, [currentUserId, familyTreeId, searchParams]);

  // When we have a pending Admin Support chat and chats list already contains it, select it (e.g. chat already existed)
  useEffect(() => {
    const pendingId = pendingAdminSupportChatIdRef.current;
    if (!pendingId || !chats.length) return;
    const found = chats.find((c) => c.id === pendingId);
    if (found) {
      pendingAdminSupportChatIdRef.current = null;
      setIsOpeningAdminSupport(false);
      setSelectedChat(found);
      if (window.matchMedia("(max-width: 768px)").matches) {
        setMobileShowChat(true);
      }
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("open");
        return next;
      });
    }
  }, [chats, setSearchParams]);

  // Subscribe to chats to check if user has any chats
  useEffect(() => {
    if (!currentUserId) {
      console.log("No currentUserId, skipping chat subscription");
      setChatsLoading(false);
      return;
    }

    console.log("Subscribing to chats for user:", currentUserId);
    setChatsLoading(true);
    const familyTreeIdLocal = getFamilyTreeIdFromStorage();

    if (!familyTreeIdLocal) {
      console.error("No family tree id found in localStorage");
      setChatsLoading(false);
      return;
    }

    const unsubscribe = subscribeToUserChats(currentUserId, familyTreeIdLocal, (chatsList) => {
      console.log("Received chats list:", chatsList.length, chatsList);
      setChats(chatsList);
      setChatsLoading(false);
      setHasChats(chatsList.length > 0);

      const pendingId = pendingAdminSupportChatIdRef.current;
      const adminSupportChat = pendingId ? chatsList.find((c) => c.id === pendingId) : null;

      if (adminSupportChat) {
        pendingAdminSupportChatIdRef.current = null;
        setIsOpeningAdminSupport(false);
        setSelectedChat(adminSupportChat);
        if (window.matchMedia("(max-width: 768px)").matches) {
          setMobileShowChat(true);
        }
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.delete("open");
          return next;
        });
      } else {
        setSelectedChat((prevSelected) => {
          if (!prevSelected && chatsList.length > 0) {
            console.log("Auto-selecting first chat:", chatsList[0].id);
            // On mobile keep list visible; selection is for highlight only until tap
            return chatsList[0];
          }
          const fresh = prevSelected
            ? chatsList.find((c) => c.id === prevSelected.id)
            : null;
          if (prevSelected && !fresh) {
            return chatsList.length > 0 ? chatsList[0] : null;
          }
          // Keep selected chat in sync with Firestore (block state, last message, etc.)
          if (fresh) return fresh;
          return prevSelected;
        });
      }
    });

    return () => {
      console.log("Unsubscribing from chats");
      if (unsubscribe) unsubscribe();
    };
  }, [currentUserId, setSearchParams]);

  // Set up global function for opening new chat modal
  useEffect(() => {
    window.openNewChatModal = () => setShowPopup(true);
    window.openEditGroupModal = (chat) => {
      if (chat?.type === "group" && chat?.id) {
        setEditGroupChat(chat);
        setChatMode("group");
      }
    };
    return () => {
      delete window.openNewChatModal;
      delete window.openEditGroupModal;
    };
  }, []);

  const handleOption = (mode) => {
    console.log(mode);
    setShowPopup(false);
    setChatMode(mode); // Open the ChatModal with the chosen mode
  };

  const closeChatModal = (chatId) => {
    setChatMode("");
    setEditGroupChat(null);
    // If a chat was created, select it after a delay to allow Firestore to update
    if (chatId) {
      setTimeout(() => {
        // Find the chat in the updated chats list
        const newChat = chats.find((c) => c.id === chatId);
        if (newChat) {
          setSelectedChat(newChat);
          if (window.matchMedia("(max-width: 768px)").matches) {
            setMobileShowChat(true);
          }
        } else {
          // If not found yet, try again after a longer delay
          setTimeout(() => {
            const updatedChat = chats.find((c) => c.id === chatId);
            if (updatedChat) {
              setSelectedChat(updatedChat);
              if (window.matchMedia("(max-width: 768px)").matches) {
                setMobileShowChat(true);
              }
            }
          }, 1000);
        }
      }, 500);
    }
  };

  const handleChatSelect = (chat) => {
    setSelectedChat(chat);
    if (isMobileMessages) {
      setMobileShowChat(true);
    }
  };

  const handleMobileBackToList = () => {
    setMobileShowChat(false);
  };

  // Show empty state if no chats (or brief loading when opening Admin Support)
  if (!hasChats) {
    return (
      <div className="admin-page-container">
        <div className="messages-empty">
          {isOpeningAdminSupport ? (
            <>
              <h2>Opening Admin Support...</h2>
              <p>Setting up your chat with admin. One moment.</p>
            </>
          ) : (
            <>
              <img
                src={window.getPublicUrl("/assets/images/no-chat-illustration.png")}
                alt="No Chat Available"
              />
              <h2>No Chat Available</h2>
              <p>
              To begin messaging, start a new conversation thread by selecting individual or group chat. <br />
              A message can be sent to the admin using this same section if you have ideas you wish to share or have any questions..
              </p>
              <button className="start-chat-btn" onClick={() => setShowPopup(true)}>
                Start New Chat
              </button>
            </>
          )}
        </div>

        {/*Chat option Modal */}
        <Modal
          isOpen={showPopup}
          onRequestClose={() => setShowPopup(false)}
          overlayClassName="popup-overlay invite-overlay"
          className="chat-popup-modal"
          portalClassName="messages-chat-modal messages-chat-modal--start"
          ariaHideApp={false}
        >
          <StartChatModal
            onClose={() => setShowPopup(false)}
            onOption={handleOption}
          />
        </Modal>

        {/*Common Chat for individual or group */}
        <Modal
          isOpen={chatMode !== ""}
          onRequestClose={() => closeChatModal()}
          overlayClassName="popup-overlay invite-overlay"
          className="chat-table-popup-modal"
          portalClassName="messages-chat-modal messages-chat-modal--picker"
          ariaHideApp={false}
        >
          <ChatModal
            onClose={(chatId) => closeChatModal(chatId)}
            chatMode={chatMode}
            family_tree_id={familyTreeId}
            editGroupChat={editGroupChat}
          />
        </Modal>
      </div>
    );
  }

  // Show chat UI when there are chats
  const chatCollapsedHalfWidth = !isMobileMessages && isChatListCollapsed && chatColumnWidthPx == null;
  const chatColumnStyle = isMobileMessages
    ? { flex: "1 1 auto", width: "100%", maxWidth: "100%" }
    : chatCollapsedHalfWidth
      ? { flex: "0 0 50%", width: "50%", maxWidth: "100%" }
      : chatColumnWidthPx != null
        ? { flex: "0 0 auto", width: `${chatColumnWidthPx}px`, maxWidth: "100%" }
        : undefined;

  const mobileShellClass = isMobileMessages
    ? mobileShowChat
      ? " messages-mobile--chat"
      : " messages-mobile--list"
    : "";

  return (
    <div
      className={`dashboard-container dashboard-detail-page messages-page${
        isMobileMessages && mobileShowChat ? " messages-page--thread" : ""
      }`}
    >
      <div className="dash-box dashboard-detail-box">
        <div className="messages-layout-card">
          <div
            className={`messages-layout-shell${mobileShellClass}`}
            ref={layoutShellRef}
          >
            <aside
              id="messages-chat-list-panel"
              className={`messages-chat-list-aside ${isChatListCollapsed && !isMobileMessages ? "collapsed" : ""}`}
              aria-hidden={isMobileMessages ? mobileShowChat : isChatListCollapsed}
            >
              <ChatList
                currentUserId={currentUserId}
                chats={chats}
                chatsLoading={chatsLoading}
                onChatSelect={handleChatSelect}
                selectedChatId={selectedChat?.id}
                onCollapseChatList={
                  isMobileMessages ? undefined : () => setIsChatListCollapsed(true)
                }
              />
            </aside>
            <div className="messages-chat-view-outer">
              <div
                ref={chatColumnRef}
                className={`messages-chat-view-column ${
                  !isMobileMessages && (chatColumnWidthPx != null || chatCollapsedHalfWidth)
                    ? "messages-chat-view-column--sized"
                    : ""
                }`}
                style={chatColumnStyle}
              >
                <ChatView
                  chat={selectedChat}
                  currentUserId={currentUserId}
                  onBack={isMobileMessages ? handleMobileBackToList : null}
                  chatListCollapsed={isChatListCollapsed}
                  onExpandChatList={
                    isMobileMessages ? undefined : () => setIsChatListCollapsed(false)
                  }
                />
                {!isMobileMessages && (
                  <button
                    type="button"
                    className="messages-chat-column-resize-handle"
                    aria-label="Resize chat panel"
                    title="Drag to resize the chat panel. With the conversation list hidden, chat uses 50% width. Double-click clears a custom width."
                    onPointerDown={handleResizePointerDown}
                    onDoubleClick={handleResizeDoubleClick}
                  />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/*Chat option Modal */}
      <Modal
        isOpen={showPopup}
        onRequestClose={() => setShowPopup(false)}
        overlayClassName="popup-overlay invite-overlay"
        className="chat-popup-modal"
        portalClassName="messages-chat-modal messages-chat-modal--start"
        ariaHideApp={false}
      >
        <StartChatModal
          onClose={() => setShowPopup(false)}
          onOption={handleOption}
        />
      </Modal>

      {/*Common Chat for individual or group */}
      <Modal
        isOpen={chatMode !== ""}
        onRequestClose={() => closeChatModal()}
        overlayClassName="popup-overlay invite-overlay"
        className="chat-table-popup-modal"
        portalClassName="messages-chat-modal messages-chat-modal--picker"
        ariaHideApp={false}
      >
        <ChatModal
          onClose={(chatId) => closeChatModal(chatId)}
          chatMode={chatMode}
          family_tree_id={familyTreeId}
          editGroupChat={editGroupChat}
        />
      </Modal>
    </div>
  );
}

export default Message;
